import { createPwaAdapter } from './pwa/PwaAdapter.js';
import { createTmaAdapter } from './tma/TmaAdapter.js';

/**
 * Comprehensive Telegram-environment detection.
 *
 * Single source of truth for "are we running inside the Telegram webview?".
 * Pure function — only inspects globals, no network, no storage, no Vue. Safe
 * to call from pre-Vue boot code (main.js) and from any module that needs the
 * platform branch.
 *
 * Signals (any truthy → TMA):
 *  - window.Telegram.WebApp.initData       — Telegram Mini Apps SDK injection
 *  - window.TelegramWebviewProxy*          — desktop / older webview bridges
 *  - window.TelegramGameProxy*             — legacy game-mode bridge
 *  - window.telegramEnv                    — bootstrap flag set by src/index.html
 *                                            (covers ordering races where one of
 *                                            the above isn't injected before the
 *                                            module evaluates)
 *  - navigator.userAgent matches /Telegram(Desktop|iOS|Android)/
 *                                          — UA fallback for webviews that don't
 *                                            inject the bridge globals early.
 *                                            Narrowed from a loose 'Telegram'
 *                                            substring to avoid false positives
 *                                            from third-party UAs containing the
 *                                            word (PWA audit MEDIUM, 2026-05-17).
 *  - document.referrer.startsWith('https://t.me/')
 *                                          — direct deep-link entry. Narrowed
 *                                            from .includes('t.me') so that a
 *                                            PWA user arriving from any URL that
 *                                            happens to contain 't.me' (e.g. a
 *                                            blog post linking to a Telegram
 *                                            channel) is not misclassified as
 *                                            TMA. Only true Telegram-origin
 *                                            referrers match.
 *
 * Rationale: previously platform/index.js used 2 signals while serviceWorker-
 * Cleanup used 7 and main.js used 3, creating a window where a Telegram session
 * could be classified `pwa` here (triggering SW registration in PwaAdapter)
 * while cleanup correctly fired in main.js. Consolidating fixes that drift.
 * PWA playbook Section 1 invariant; PWA audit MEDIUM-8.
 */
const TELEGRAM_UA_RE = /Telegram(Desktop|iOS|Android)/;

export function isTelegramEnv() {
  if (typeof window === 'undefined') return false;
  return Boolean(
    window.Telegram?.WebApp?.initData ||
      window.TelegramWebviewProxy ||
      window.TelegramWebviewProxyProto ||
      window.TelegramGameProxy ||
      window.TelegramGameProxy_receiveEvent ||
      window.telegramEnv ||
      (typeof navigator !== 'undefined' && TELEGRAM_UA_RE.test(navigator.userAgent)) ||
      (typeof document !== 'undefined' && document.referrer.startsWith('https://t.me/')),
  );
}

/**
 * Where the app is running, sent to the API on every call as `X-Poolit-Surface`
 * (src/httpConfig/config/axios.js) so the admin "Supplier activity" window can
 * tell a Telegram Mini App open from an installed PWA or a plain browser tab.
 */
export function appSurface() {
  if (isTelegramEnv()) return 'tg_mini_app';
  if (typeof window === 'undefined') return 'browser';
  const standalone = window.matchMedia?.('(display-mode: standalone)').matches || window.navigator.standalone === true;
  return standalone ? 'pwa_installed' : 'browser';
}

function detectPlatform() {
  return isTelegramEnv() ? 'tma' : 'pwa';
}

export const platformType = detectPlatform();

export const adapter = platformType === 'tma'
  ? createTmaAdapter()
  : createPwaAdapter();

// Live alias — re-evaluates all seven Telegram-detection signals on every call.
// Replaces the frozen `() => platformType === 'tma'` snapshot to fix the boot-
// race where Telegram globals load after module init (PWA audit MEDIUM-2;
// playbook Section 1 invariant "TMA must NOT register a SW"; Section 5 row 9).
export const isTma = isTelegramEnv;
export const isPwa = () => platformType === 'pwa';
